import { Fragment, useEffect, useMemo, useState } from 'react'
import StepperForm, { StepFormElement } from 'src/components/StepperFormWalliro/StepperForm'
import {
  CREATE_SAFE_CATEGORY,
  CREATE_SAFE_EVENTS,
  LOAD_SAFE_CATEGORY,
  LOAD_SAFE_EVENTS,
} from 'src/utils/events/createLoadSafe'
import StepOne from './StepOne'
import StepTwo from './StepTwo'
import StepThere from './StepThere'
import StepFour from './StepFour'
import { getChainInfo, getShortName } from 'src/config'
import { useDispatch, useSelector } from 'react-redux'
import {
  availableSelector,
  providerNameSelector,
  shouldSwitchWalletChain,
  userAccountSelector,
} from 'src/logic/wallets/store/selectors'
import {
  FIELD_CREATE_CUSTOM_SAFE_NAME,
  FIELD_CREATE_SUGGESTED_SAFE_NAME,
  FIELD_MAX_OWNER_NUMBER,
  FIELD_NEW_SAFE_PROXY_SALT,
  FIELD_NEW_SAFE_THRESHOLD,
  FIELD_CREATE_SAFE_OWNERS_LIST,
  FIELD_STARTER_TYPE,
  SAFE_PENDING_CREATION_STORAGE_KEY,
  StarterFormValues,
  TypeForm,
  FIELD_LOAD_SAFE_ADDRESS,
  FIELD_LOAD_SAFE_OWNER_LIST,
  FIELD_SAFE_THRESHOLD,
  FIELD_LOAD_SUGGESTED_SAFE_NAME,
  FIELD_LOAD_IS_LOADING_SAFE_ADDRESS,
  FIELD_NEW_SAFE_OWNER_ENS_LIST,
} from './fields/starterFields'
import { currentNetworkAddressBookAsMap } from 'src/logic/addressBook/store/selectors'
import { useHistory, useLocation } from 'react-router-dom'
import { useMnemonicSafeName } from 'src/logic/hooks/useMnemonicName'
import { reverseENSLookup } from 'src/logic/wallets/getWeb3'
import queryString from 'query-string'
import styled from 'styled-components'
import { Loader } from '@gnosis.pm/safe-react-components'
import SafeCreationProcess from './components/SafeCreationProcess'
import { deepen, objectByString } from 'src/utils/objects'
import StepFive from './StepFive'
import { trackEvent } from 'src/utils/googleTagManager'
import { loadFromStorage, saveToStorage } from 'src/utils/storage'
import { loadSafeAddressStepValidations } from './StepFour/Load/LoadSafeAddressStep'
import { isValidAddress } from 'src/utils/isValidAddress'
import { getLoadSafeName, getOwnerName } from './fields/utils'
import { AddressBookEntry, makeAddressBookEntry } from 'src/logic/addressBook/model/addressBook'
import { checksumAddress } from 'src/utils/checksumAddress'
import { addressBookSafeLoad } from 'src/logic/addressBook/store/actions'
import { currentChainId } from 'src/logic/config/store/selectors'
import { buildSafe } from 'src/logic/safe/store/actions/fetchSafe'
import { loadStoredSafes, saveSafes } from 'src/logic/safe/utils'
import { addOrUpdateSafe } from 'src/logic/safe/store/actions/addOrUpdateSafe'
import {
  extractPrefixedSafeAddress,
  generateSafeRoute,
  LOAD_SPECIFIC_SAFE_ROUTE,
  SAFE_ROUTES,
  STARTER_ROUTE,
} from '../routes'
import { setChainId } from 'src/logic/config/utils'
import { ChainId } from 'src/config/chain'

const CreateWalliro = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [safePendingToBeCreated, setSafePendingToBeCreated] = useState<StarterFormValues>()
  const [initialFormValues, setInitialFormValues] = useState<{
    open?: StarterFormValues
    load?: StarterFormValues
    locationNetwork?: ChainId
  }>()
  const selectedNetwork = getChainInfo()
  const providerName = useSelector(providerNameSelector)
  const isWalletConnected = !!useSelector(availableSelector)
  const isWrongNetwork = useSelector(shouldSwitchWalletChain)
  const provider = !!providerName && !isWrongNetwork

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [typeStarter, setTypeStarter] = useState<TypeForm | undefined>()

  const userWalletAddress = useSelector(userAccountSelector)
  const addressBook = useSelector(currentNetworkAddressBookAsMap)
  const location = useLocation()
  const safeRandomName = useMnemonicSafeName()
  const chainId = useSelector(currentChainId)
  const { safeAddress } = extractPrefixedSafeAddress(undefined, LOAD_SPECIFIC_SAFE_ROUTE)

  useEffect(() => {
    const parsed = queryString.parse(location.search)
    const chainId = parsed['network']

    if (typeof chainId === 'string' && chainId) {
      setChainId(chainId)
      setInitialFormValues((prev) => ({
        ...prev,
        locationNetwork: chainId,
      }))

      history.replace(history.location.pathname)
    }
  }, [])

  const resetLocationNetwork = () => {
    setInitialFormValues((prev) => ({
      ...prev,
      locationNetwork: undefined,
    }))
  }

  const [isSubti, setIsSubti] = useState(false)

  useEffect(() => {
    const checkIfSafeIsPendingToBeCreated = async (): Promise<void> => {
      setIsLoading(true)
      const safePendingToBeCreated = await Promise.resolve(
        loadFromStorage<StarterFormValues>(SAFE_PENDING_CREATION_STORAGE_KEY),
      )

      if (provider) {
        setSafePendingToBeCreated(safePendingToBeCreated)
      }
      setIsLoading(false)
    }
    checkIfSafeIsPendingToBeCreated()
  }, [provider])

  const updateAddressBook = (values: StarterFormValues) => {
    const ownerList = objectByString(values, FIELD_LOAD_SAFE_OWNER_LIST) as AddressBookEntry[]
    const ownerEntries = ownerList
      .map((owner) => {
        const ownerName = getOwnerName(values, owner.address)
        return {
          ...owner,
          name: ownerName,
        }
      })
      .filter((owner) => !!owner.name)

    const safeEntry = makeAddressBookEntry({
      address: checksumAddress(objectByString(values, FIELD_LOAD_SAFE_ADDRESS) || ''),
      name: getLoadSafeName(values, addressBook),
      chainId,
    })

    dispatch(addressBookSafeLoad([...ownerEntries, safeEntry]))
  }

  const submitForm = async (values: StarterFormValues): Promise<void> => {
    // Track number of owners
    const typeForm = objectByString(values, 'starterType')
    if (typeForm === TypeForm.open) {
      trackEvent({
        ...CREATE_SAFE_EVENTS.OWNERS,
        label: objectByString(values, FIELD_CREATE_SAFE_OWNERS_LIST).length,
      })
      // Track threshold
      trackEvent({
        ...CREATE_SAFE_EVENTS.THRESHOLD,
        label: objectByString(values, FIELD_NEW_SAFE_THRESHOLD),
      })

      saveToStorage(SAFE_PENDING_CREATION_STORAGE_KEY, { ...values['create'] })
      setSafePendingToBeCreated(values['create'])
    } else {
      const address = objectByString(values, FIELD_LOAD_SAFE_ADDRESS)
      if (!isValidAddress(address)) {
        return
      }
      setIsSubti(true)

      // Track number of owners
      trackEvent({
        ...LOAD_SAFE_EVENTS.OWNERS,
        label: objectByString(values, FIELD_LOAD_SAFE_OWNER_LIST).length,
      })

      const threshold = objectByString(values, FIELD_SAFE_THRESHOLD)
      if (threshold) {
        // Track threshold
        trackEvent({
          ...LOAD_SAFE_EVENTS.THRESHOLD,
          label: threshold,
        })
      }

      trackEvent(LOAD_SAFE_EVENTS.GO_TO_SAFE)

      updateAddressBook(values)

      const checksummedAddress = checksumAddress(address || '')
      const safeProps = await buildSafe(checksummedAddress)
      const storedSafes = loadStoredSafes() || {}
      storedSafes[checksummedAddress] = safeProps

      saveSafes(storedSafes)
      dispatch(addOrUpdateSafe(safeProps))

      // Go to the newly added Safe
      history.push(
        generateSafeRoute(SAFE_ROUTES.DASHBOARD, {
          shortName: getShortName(),
          safeAddress: checksummedAddress,
        }),
      )
    }
  }

  useEffect(() => {
    const initialValues: any = deepen({
      [FIELD_LOAD_SUGGESTED_SAFE_NAME]: safeRandomName,
      [FIELD_LOAD_SAFE_ADDRESS]: safeAddress,
      [FIELD_LOAD_IS_LOADING_SAFE_ADDRESS]: false,
      [FIELD_LOAD_SAFE_OWNER_LIST]: [],
    })
    setInitialFormValues((prev) => ({
      ...prev,
      load: initialValues,
    }))
  }, [safeAddress, safeRandomName])

  useEffect(() => {
    let isCurrent = true
    if (location.state === 'reset') {
      setSafePendingToBeCreated(undefined)
      history.push({
        pathname: STARTER_ROUTE,
        state: undefined,
      })
    }
    if (provider && userWalletAddress) {
      const getInitValues = async () => {
        const initialValuesFromUrl = await getInitialValues(userWalletAddress, addressBook, location, safeRandomName)
        if (isCurrent) {
          setInitialFormValues((prev) => ({
            ...prev,
            open: initialValuesFromUrl,
          }))
        }
      }

      if (!isSubti) {
        getInitValues()
      }
    }
    return () => {
      isCurrent = false
    }
  }, [provider, userWalletAddress, addressBook, location, safeRandomName, isSubti])

  const isInitializing = !provider || !initialFormValues?.open

  return !!safePendingToBeCreated ? (
    <SafeCreationProcess />
  ) : (
    <StepperForm
      initialValues={{
        ...initialFormValues?.load,
        create: initialFormValues?.open?.['create'],
      }}
      onSubmit={submitForm}
      testId={'create-safe-form'}
      // trackingCategory={CREATE_SAFE_CATEGORY}
    >
      <StepFormElement
        label={'Walliro supports a wide variety of wallets that you can choose them to interact with'}
        nextButtonLabel="Continue"
        disableNextButton={!selectedNetwork.chainId || isLoading}
      >
        {isLoading ? (
          <LoaderContainer data-testid={'create-safe-loader'}>
            <Loader size="md" />
          </LoaderContainer>
        ) : (
          <StepOne
            selectedNetwork={selectedNetwork}
            locationNetwork={initialFormValues?.locationNetwork}
            resetLocationNetwork={resetLocationNetwork}
          />
        )}
      </StepFormElement>
      <StepFormElement
        label={'Connect Wallet'}
        nextButtonLabel="Continue"
        disableNextButton={!isWalletConnected || isWrongNetwork || isInitializing}
      >
        <StepTwo isWalletConnected={isWalletConnected} isWrongNetwork={isWrongNetwork} />
      </StepFormElement>
      <StepFormElement label={'Create new Walliro Or Load existing Walliro'} nextButtonLabel="Continue">
        <StepThere
          callback={(type) => {
            setIsSubti(false)
            setTypeStarter(type)
          }}
        />
      </StepFormElement>
      <StepFormElement
        label={typeStarter === TypeForm.open ? 'Create new Walliro' : 'Load existing Walliro'}
        nextButtonLabel="Continue"
        validate={typeStarter === TypeForm.open ? undefined : (values) => loadSafeAddressStepValidations(values)}
      >
        <StepFour />
      </StepFormElement>
      <StepFormElement label={'Review'} nextButtonLabel={typeStarter === TypeForm.open ? 'Create' : 'Load'}>
        <StepFive />
      </StepFormElement>
    </StepperForm>
  )
}

export default CreateWalliro

const DEFAULT_THRESHOLD_VALUE = 1

async function getInitialValues(userAddress, addressBook, location, suggestedSafeName): Promise<StarterFormValues> {
  const query = queryString.parse(location.search, { arrayFormat: 'comma' })
  const { name, owneraddresses, ownernames, threshold } = query

  // if owners are not present in the URL we use current user account as default owner
  const isOwnersPresentInTheUrl = !!owneraddresses
  const ownersFromUrl = Array.isArray(owneraddresses) ? owneraddresses : [owneraddresses]
  const owners = isOwnersPresentInTheUrl ? ownersFromUrl : [userAddress]

  // we set the owner names
  const ownersNamesFromUrl = Array.isArray(ownernames) ? ownernames : [ownernames]
  const userAddressName = [addressBook[userAddress]?.name || '']
  const ownerNames = isOwnersPresentInTheUrl ? ownersNamesFromUrl : userAddressName

  const thresholdFromURl = Number(threshold)
  const isValidThresholdInTheUrl =
    threshold && !Number.isNaN(threshold) && thresholdFromURl <= owners.length && thresholdFromURl > 0

  return deepen({
    [FIELD_CREATE_SUGGESTED_SAFE_NAME]: suggestedSafeName,
    [FIELD_CREATE_CUSTOM_SAFE_NAME]: name,
    [FIELD_NEW_SAFE_THRESHOLD]: isValidThresholdInTheUrl ? threshold : DEFAULT_THRESHOLD_VALUE,
    [FIELD_CREATE_SAFE_OWNERS_LIST]: owners.map((owner, index) => ({
      nameFieldName: `owner-name-${index}`,
      addressFieldName: `owner-address-${index}`,
    })),
    [FIELD_NEW_SAFE_OWNER_ENS_LIST]: (
      await Promise.all(
        owners.map(async (address) => {
          return { [address]: await reverseENSLookup(address) }
        }),
      )
    ).reduce((acc, owner) => {
      return { ...acc, ...owner }
    }, {}),
    // we set owners address values as owner-address-${index} format in the form state
    ...owners.reduce(
      (ownerAddressFields, ownerAddress, index) => ({
        ...ownerAddressFields,
        [`create.owner-address-${index}`]: ownerAddress,
      }),
      {},
    ),
    // we set owners name values as owner-name-${index} format in the form state
    ...ownerNames.reduce(
      (ownerNameFields, ownerName, index) => ({
        ...ownerNameFields,
        [`create.owner-name-${index}`]: ownerName,
      }),
      {},
    ),
    [FIELD_MAX_OWNER_NUMBER]: owners.length,
    [FIELD_NEW_SAFE_PROXY_SALT]: Date.now(),
  }) as StarterFormValues
}

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`
